import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Home,
  Info,
  LayoutGrid,
  Calendar,
  BookOpen,
  HelpCircle,
  LogOut, 
  Shield, 
  Menu, 
  X, 
  Settings,
  Bell,
  ChevronDown,
  Gauge,
  Watch,
  Compass,
  MessageCircle,
  LifeBuoy,
  Phone,
  LayoutDashboard,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { getUserProfile } from '@/lib/db';
import { cn } from '@/lib/utils';
import logoHED from '../assets/logo-hed.png';
import { useToolTheme } from '@/contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { UpdateCenter } from './updates/UpdateCenter';
import { supabase } from '@/lib/supabase';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
  children?: NavItem[];
}

interface MenuVisibility {
  mainNav: boolean;
  userMenu: boolean;
  footer: boolean;
  socialLinks: boolean;
  languageSelector: boolean;
  themeSelector: boolean;
}

interface ToolVisibility {
  barometre: boolean;
  projectorWatch: boolean;
  align: boolean;
}

const DEFAULT_MENU_VISIBILITY = {
  mainNav: true,
  userMenu: true,
  footer: true,
  socialLinks: true,
  languageSelector: true,
  themeSelector: true
};

const DEFAULT_TOOL_VISIBILITY = {
  barometre: true,
  projectorWatch: false,
  align: false
};

const baseNavItems: NavItem[] = [
  {
    label: "Accueil",
    path: "/",
    icon: <Home className="w-4 h-4" />
  },
  {
    label: "Outils HED",
    path: "/tools",
    icon: <LayoutGrid className="w-4 h-4" />,
    children: [
      {
        label: "Baromètre HED",
        path: "/barometre",
        icon: <Gauge className="w-4 h-4" />
      },
      {
        label: "Projector Watch",
        path: "/projector-watch",
        icon: <Watch className="w-4 h-4" />
      },
      {
        label: "Align",
        path: "/align",
        icon: <Compass className="w-4 h-4" />
      }
    ]
  },
  {
    label: "Consultations",
    path: "/consultations",
    icon: <Calendar className="w-4 h-4" />
  },
  {
    label: "Ressources & Support",
    path: "/resources",
    icon: <LifeBuoy className="w-4 h-4" />,
    children: [
      {
        label: "Forum",
        path: "/forum",
        icon: <MessageCircle className="w-4 h-4" />
      },
      {
        label: "FAQ",
        path: "/help/faq",
        icon: <HelpCircle className="w-4 h-4" />
      },
      {
        label: "Contact",
        path: "/contact-us",
        icon: <Phone className="w-4 h-4" />
      },
      {
        label: "À propos",
        path: "/about",
        icon: <Info className="w-4 h-4" />
      },
      {
        label: "Ressources",
        path: "/resources",
        icon: <BookOpen className="w-4 h-4" />
      }
    ]
  }
];

const adminNavItem: NavItem = {
  label: "Administration",
  path: "/admin",
  icon: <Shield className="w-4 h-4" />,
  adminOnly: true
};

const barometreMenu = [
  {
    label: "Présentation",
    path: "/barometre",
    icon: <Info className="w-4 h-4" />
  },
  {
    label: "Diagnostic",
    path: "/barometre/assessment",
    icon: <Gauge className="w-4 h-4" />
  },
  {
    label: "Tableau de bord",
    path: "/barometre/dashboard",
    icon: <LayoutDashboard className="w-4 h-4" />
  },
  {
    label: "Paramètres",
    path: "/barometre/settings",
    icon: <Settings className="w-4 h-4" />
  }
];

const NavItem = ({ item, isActive, onClose, toolVisibility }: { 
  item: NavItem; 
  isActive: boolean;
  onClose: () => void;
  toolVisibility: ToolVisibility;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const { toolColors } = useToolTheme();

  const visibleChildren = item.children?.filter(child => {
    if (child.path === '/barometre') return toolVisibility.barometre;
    if (child.path === '/projector-watch') return toolVisibility.projectorWatch;
    if (child.path === '/align') return toolVisibility.align;
    return true;
  });

  return (
    <div 
      className="relative group"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <Link
        to={hasChildren ? '#' : item.path}
        onClick={(e) => {
          if (hasChildren && e.target === e.currentTarget) {
            e.preventDefault();
            setIsOpen(!isOpen);
          } else {
            onClose();
          }
        }}
        className={cn(
          "flex items-center justify-between gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors w-full",
          isActive
            ? `bg-[${toolColors.primary}]/10 text-[${toolColors.primary}]`
            : `text-gray-700 hover:text-[${toolColors.primary}] hover:bg-[${toolColors.primary}]/5`
        )}
        style={{
          backgroundColor: isActive ? `${toolColors.primary}10` : undefined,
          color: isActive ? toolColors.primary : undefined,
          '--hover-bg': `${toolColors.primary}05`,
          '--hover-color': toolColors.primary,
        }}
      >
        <div className="flex items-center gap-2">
          {item.icon}
          <span>{item.label}</span>
        </div>
        {hasChildren && visibleChildren && visibleChildren.length > 0 && (
          <ChevronDown className={cn(
            "w-4 h-4 transition-transform",
            isOpen && "transform rotate-180"
          )} />
        )}
      </Link>

      {hasChildren && isOpen && visibleChildren && visibleChildren.length > 0 && (
        <div className={cn(
          "absolute left-0 mt-0 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50",
          "md:group-hover:block",
          {
            "relative md:absolute": true,
            "block": isOpen
          }
        )}>
          <div className="py-1">
            {visibleChildren.map((child) => (
              <Link
                key={child.path}
                to={child.path}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-[var(--hover-bg)] hover:text-[var(--hover-color)]"
                style={{
                  '--hover-bg': `${toolColors.primary}05`,
                  '--hover-color': toolColors.primary,
                }}
                onClick={onClose}
              >
                {child.icon}
                <span>{child.label}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

function Navbar() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { currentTool, toolColors } = useToolTheme();
  const [showFloatingMenu, setShowFloatingMenu] = useState(false);
  const [isFloatingMenuCollapsed, setIsFloatingMenuCollapsed] = useState(false);
  const [isFloatingMenuVisible, setIsFloatingMenuVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [menuVisibility, setMenuVisibility] = useState<MenuVisibility>(DEFAULT_MENU_VISIBILITY);
  const [toolVisibility, setToolVisibility] = useState<ToolVisibility>(DEFAULT_TOOL_VISIBILITY);

  const navItems = useMemo(() => {
    const items = [...baseNavItems];
    if (isAdmin) {
      items.push(adminNavItem);
    }
    return items;
  }, [isAdmin]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Charger la visibilité des menus
        const { data: menuSettings, error: menuError } = await supabase
          .from('admin_settings')
          .select('value')
          .eq('key', 'menu_visibility')
          .maybeSingle();

        if (menuError && menuError.code !== 'PGRST116') {
          console.error('Error loading menu visibility:', menuError);
          return;
        }

        if (menuSettings?.value) {
          setMenuVisibility(menuSettings.value);
        }

        // Charger la visibilité des outils
        const { data: toolSettings, error: toolError } = await supabase
          .from('admin_settings')
          .select('value')
          .eq('key', 'tool_visibility')
          .maybeSingle();

        if (toolError && toolError.code !== 'PGRST116') {
          console.error('Error loading tool visibility:', toolError);
          return;
        }

        if (toolSettings?.value) {
          setToolVisibility(toolSettings.value);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };

    loadSettings();

    // Subscribe to real-time updates
    const subscription = supabase
      .channel('settings_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'admin_settings' },
        () => {
          loadSettings();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsFloatingMenuVisible(currentScrollY <= lastScrollY || currentScrollY < 100);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    setShowFloatingMenu(location.pathname.startsWith('/barometre'));
  }, [location]);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (currentUser?.id) {
        try {
          const profile = await getUserProfile(currentUser.id);
          setIsAdmin(profile?.email === 'solutions.oko@gmail.com');
        } catch (error) {
          console.error('Error checking admin status:', error);
        }
      }
    };

    checkAdminStatus();
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: "Déconnexion réussie",
        description: "À bientôt !",
      });
      navigate('/login');
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Une erreur est survenue lors de la déconnexion.",
      });
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const navbarStyle = {
    borderColor: `${toolColors.primary}20`,
    transition: 'border-color 0.5s ease-in-out, background-color 0.5s ease-in-out'
  };

  return (
    <>
      <motion.nav 
        className="bg-white border-b fixed w-full top-0 z-50"
        style={navbarStyle}
        initial={{ borderColor: '#e5e7eb' }}
        animate={{ borderColor: `${toolColors.primary}20` }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-14">
            <div className="flex items-center gap-8">
              <Link to="/" className="flex items-center">
                <img 
                  src={logoHED}
                  alt="Logo HED" 
                  className="h-8 w-auto object-contain"
                />
              </Link>
              
              {menuVisibility.mainNav && (
                <div className="hidden md:flex items-center gap-1">
                  <UpdateCenter />
                  {navItems.map((item) => {
                    if (item.adminOnly && !isAdmin) return null;
                    const isActive = location.pathname === item.path;
                    return (
                      <NavItem
                        key={item.path}
                        item={item}
                        isActive={isActive}
                        onClose={closeMobileMenu}
                        toolVisibility={toolVisibility}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            {menuVisibility.userMenu && (
              <div className="hidden md:flex items-center gap-2">
                {currentUser ? (
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Déconnexion</span>
                  </button>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                    >
                      Connexion
                    </Link>
                    <Link
                      to="/register"
                      className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors"
                      style={{
                        background: `linear-gradient(to right, ${toolColors.primary}, ${toolColors.secondary})`,
                      }}
                    >
                      Inscription
                    </Link>
                  </>
                )}
              </div>
            )}

            <div className="flex items-center md:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleMobileMenu}
                className="text-gray-500 hover:text-gray-600"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </Button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="fixed inset-x-0 top-14 bg-white shadow-lg md:hidden z-50"
            >
              <div className="p-4 space-y-1">
                {navItems.map((item) => {
                  if (item.adminOnly && !isAdmin) return null;
                  const isActive = location.pathname === item.path;
                  return (
                    <NavItem
                      key={item.path}
                      item={item}
                      isActive={isActive}
                      onClose={closeMobileMenu}
                      toolVisibility={toolVisibility}
                    />
                  );
                })}
                
                {currentUser ? (
                  <button
                    onClick={() => {
                      handleLogout();
                      closeMobileMenu();
                    }}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors w-full"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Déconnexion</span>
                  </button>
                ) : (
                  <div className="space-y-2 pt-2">
                    <Link
                      to="/login"
                      className="block px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                      onClick={closeMobileMenu}
                    >
                      Connexion
                    </Link>
                    <Link
                      to="/register"
                      className="block px-4 py-2 text-sm font-medium text-white rounded-md transition-colors"
                      style={{
                        background: `linear-gradient(to right, ${toolColors.primary}, ${toolColors.secondary})`,
                      }}
                      onClick={closeMobileMenu}
                    >
                      Inscription
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      <AnimatePresence>
        {showFloatingMenu && isFloatingMenuVisible && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={`fixed ${isFloatingMenuCollapsed ? 'bottom-20' : 'bottom-6'} right-6 z-40`}
          >
            <div className="relative">
              <Button
                variant="default"
                size="icon"
                onClick={() => setIsFloatingMenuCollapsed(!isFloatingMenuCollapsed)}
                className="w-12 h-12 rounded-full shadow-lg"
                style={{ 
                  backgroundColor: toolColors.primary,
                  color: 'white'
                }}
              >
                {isFloatingMenuCollapsed ? (
                  <ChevronLeft className="w-6 h-6" />
                ) : (
                  <ChevronRight className="w-6 h-6" />
                )}
              </Button>

              <AnimatePresence>
                {!isFloatingMenuCollapsed && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="absolute bottom-0 right-16 flex flex-col gap-2"
                  >
                    {barometreMenu.map((item, index) => (
                      <motion.div
                        key={item.path}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ delay: index * 0.1 }}
                        className="group relative"
                      >
                        <Link
                          to={item.path}
                          className={cn(
                            "flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-colors",
                            location.pathname === item.path
                              ? "text-white"
                              : "bg-white text-gray-600 hover:bg-gray-50"
                          )}
                          style={{
                            backgroundColor: location.pathname === item.path ? toolColors.primary : undefined
                          }}
                        >
                          {item.icon}
                          <span className="absolute right-14 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none transition-opacity">
                            {item.label}
                          </span>
                        </Link>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Navbar;